import type { OperatingHoursCalc } from '@pperform/pp-data-mappers';

// Assumptions
// All DAYS_OF_WEEK are returned from backend, operatingDay is required, firstOpenHours and secondOpenHours are optional
// DaysOfWeek are in English
// First day of week is Sunday

export const isAvailableToday = (dayOfWeek: string) => {
  const DAYS_OF_WEEK = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const TODAY = new Date().getDay();
  return DAYS_OF_WEEK[TODAY] === dayOfWeek;
};

interface OpeningHour {
  operatingDay: string;
  timeZone: string;
  firstOpenHours: string;
  secondOpenHours: string;
  firstCutoff: string;
  secondCutoff: string;
}
type OperatingHoursMap = Record<
  string,
  {
    serviceName: string;
    openingHours: Record<string, OpeningHour>;
  }
>;
export interface OperatingHoursByService {
  serviceName: string;
  openingHours: OpeningHour[];
}
export const buildOperatingHoursByService = (
  operatingHoursCalc: OperatingHoursCalc[],
): OperatingHoursByService[] => {
  // filter out operatingHoursCalc that don't have firstOpenHours and secondOpenHours
  const filteredOperatingHours = operatingHoursCalc.filter(
    (item) => item.firstOpenHours || item.secondOpenHours,
  );

  if (!filteredOperatingHours.length) {
    return [];
  }

  const DAYS_OF_WEEK = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ] as const;

  // Always show operating hour for all 7 days of the week
  const generateOpeningHours = () => {
    return DAYS_OF_WEEK.reduce((acc, day) => {
      acc[day] = { operatingDay: day, firstOpenHours: '', secondOpenHours: '' };
      return acc;
    }, {});
  };

  const operatingHours: OperatingHoursMap = {};

  filteredOperatingHours.forEach((operatingHour) => {
    const { serviceName, operatingDay, ...rest } = operatingHour;

    if (!operatingHours[serviceName]) {
      operatingHours[serviceName] = {
        serviceName,
        openingHours: generateOpeningHours(),
      };
    }

    operatingHours[serviceName].openingHours[operatingDay] = {
      operatingDay,
      ...rest,
    };
  });

  return Object.values(operatingHours).map((item) => {
    return {
      serviceName: item.serviceName,
      openingHours: Object.values(item.openingHours),
    };
  });
};
